import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  PaginationState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { TableProps } from '$shared/components/Table/Table.interface';
import { classNames } from '$utils';
import './Table.sass';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pagination } from '$shared/components/Table/Pagination/Pagination';
import { SearchInput, Tabs, TabsItem } from '$uikit';
import { IcAngleDownFill16, IcAngleUp16, IcFolderArrow28, IcSearch24 } from '$assets';
import debounce from 'lodash/debounce';
import { useIsFirstRender } from '$hooks';

export const Table = ({
  data,
  columns,
  className,
  cellPadding,
  onRowClick,
  paginationConfig,
  isLayoutFixed,
  isSearchable,
  onTableParamsChange,
  filterTabs,
  initialFilterTab,
  enableSorting,
  initialSorting,
}: TableProps<typeof data>) => {
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: paginationConfig?.pageIndex || 0,
    pageSize: paginationConfig?.pageSize || 10,
  });
  const [sorting, setSorting] = useState<SortingState>(initialSorting || []);
  const isFirstRender = useIsFirstRender();
  const [searchValue, setSearchValue] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [filterTab, setFilterTab] = useState(
    initialFilterTab || (filterTabs && filterTabs[0]),
  );
  const isResetPagination = useRef(false);

  const pagination = useMemo(
    () =>
      paginationConfig && {
        pageIndex,
        pageSize,
      },
    [pageIndex, pageSize],
  );

  const sortingColumns = useMemo(() => {
    const res: { [index: string]: boolean } = {};
    sorting.forEach((item) => {
      res[item.id] = item.desc;
    });
    return res;
  }, [sorting]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    enableSorting,
    manualPagination: true,
    onPaginationChange: setPagination,
    pageCount: paginationConfig?.totalCount || 0,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    sortingFns: {
      // @ts-ignore
      diffPercent: (a, b, columnId: string) => {
        const aVal = a.original[columnId];
        const bVal = b.original[columnId];

        const diff1 = aVal[0] - aVal[1];
        const p1 = aVal[1] === 0 ? 0 : parseInt(`${(diff1 / (aVal[1] + 1)) * 100}`, 10);

        const diff2 = bVal[0] - bVal[1];
        const p2 = bVal[1] === 0 ? 0 : parseInt(`${(diff2 / (bVal[1] + 1)) * 100}`, 10);

        if (sortingColumns[columnId]) {
          if (p1 === p2) {
            return 0;
          } else if (p1 > p2) {
            return 1;
          } else {
            return -1;
          }
        } else {
          if (p1 === p2) {
            return 0;
          } else if (p1 > p2) {
            return -1;
          } else {
            return 1;
          }
        }
      },
    },
  });

  const handleChangeData = (pageIndex = 0) => {
    onTableParamsChange &&
      onTableParamsChange({ page: pageIndex + 1, pageSize, searchFilter, filterTab });
  };

  const resetPagination = () => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  useEffect(() => {
    if (isFirstRender) {
      return;
    }

    handleChangeData();
    resetPagination();
    isResetPagination.current = true;
  }, [searchFilter, filterTab]);

  useEffect(() => {
    !isResetPagination.current && handleChangeData(pageIndex);
  }, [pageIndex, pageSize]);

  const debouncedChangeGlobalFilter = useCallback(
    debounce((value) => {
      setSearchFilter(value);
    }, 300),
    [],
  );

  useEffect(() => {
    debouncedChangeGlobalFilter(searchValue);
  }, [searchValue]);

  useEffect(() => {
    isResetPagination.current = false;
  }, [data]);

  return (
    <div className="Table__wrap">
      {isSearchable && (
        <div className="Table__Search">
          <SearchInput
            query={searchValue}
            onChange={setSearchValue}
            placeholder="Search..."
            placeholderIcon={<IcSearch24 className="Table__Search__icon" />}
          />
        </div>
      )}
      <div className="Table__Tabs">
        {filterTabs && filterTab && (
          <Tabs
            selected={filterTab}
            onChange={(tab) => {
              setFilterTab(tab);
            }}
          >
            {filterTabs.map((tab) => (
              <TabsItem key={tab} value={tab}>
                {tab}
              </TabsItem>
            ))}
          </Tabs>
        )}
      </div>
      <table
        className={classNames('Table', className, {
          _isLayoutFixed: isLayoutFixed,
        })}
        cellPadding={cellPadding ? `${cellPadding}px` : ''}
      >
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const { id, isPlaceholder } = header;
                const sorted = header.column.getIsSorted();
                return (
                  <th
                    key={id}
                    id={id}
                    className={classNames('Table__header__cell', {
                      _isCanSort: header.column.getCanSort(),
                      _isSorted: !!sorted,
                    })}
                    style={{ width: header.column.getSize() }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                    {sorted && (
                      <div className={classNames('Table__sorted', sorted)}>
                        <IcAngleDownFill16 />
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} onClick={() => onRowClick && onRowClick(row)}>
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  id={cell.column.id}
                  style={{ width: cell.column.getSize() }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {paginationConfig && !!paginationConfig.totalCount && (
        <div className="Table__pagination__wrap">
          <Pagination
            currentPage={pageIndex + 1}
            totalCount={paginationConfig.totalCount}
            onChangePage={(selectedPage) => {
              table.setPageIndex(selectedPage - 1);
            }}
            pageSizeConfig={
              paginationConfig.pageSize
                ? {
                    pageSize,
                    onChangePageSize: table.setPageSize,
                    totalItemsCount: paginationConfig.totalItemsCount,
                    pageSizeOptions: paginationConfig.pageSizeOptions,
                  }
                : null
            }
          />
        </div>
      )}
    </div>
  );
};
